import { useCallback, useState } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Dialog, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "src/locales";
import LabGraph, { DetailItem, StatusType, TrendDirection } from "./lab-graph";
import LabRequestDetail from "./lab-request-detail";

type LabCard = {
  title: string;
  value: string;
  trend: string;
  direction: TrendDirection;
  bars: number[];
  status: string;
  statusType: StatusType;
  detailDate: string;
  detailItems?: DetailItem[];
};

type FilterOption = "out-of-range" | "normal" | "all";

const filterPills: Array<{ key: FilterOption; label: string; minWidth: number }> = [
  { key: "out-of-range", label: "Referans Dışı Sonuçlar", minWidth: 176 },
  { key: "normal", label: "Normal Sonuçlar", minWidth: 148 },
  { key: "all", label: "Tüm Sonuçlar", minWidth: 151 },
];

const labCards: LabCard[] = [
  {
    title: "İç, Serum",
    value: "50",
    trend: "14.5%",
    direction: "up",
    bars: [9, 14, 20],
    status: "Referans Dışı(H)",
    statusType: "high",
    detailDate: "17 Şubat 2026",
  },
  {
    title: "CRP",
    value: "50",
    trend: "14.5%",
    direction: "down",
    bars: [9, 14, 20],
    status: "Normal",
    statusType: "normal",
    detailDate: "17 Şubat 2026",
  },
  {
    title: "Hemogram",
    value: "50",
    trend: "14.5%",
    direction: "up",
    bars: [9, 14, 20],
    status: "Normal",
    statusType: "normal",
    detailDate: "17 Şubat 2026",
    detailItems: [
      { code: "wbc", value: "50", status: "Yüksek", type: "high" },
      { code: "RBC", value: "25", status: "Yüksek", type: "high" },
      { code: "HGB", value: "50", status: "Yüksek", type: "high" },
      { code: "HCT", value: "50", status: "Normal", type: "normal" },
      { code: "MCV", value: "50", status: "Normal", type: "normal" },
      { code: "NEU", value: "50", status: "Normal", type: "normal" },
      { code: "PLT", value: "50", status: "Normal", type: "normal" },
    ],
  },
  {
    title: "TSH",
    value: "50",
    trend: "14.5%",
    direction: "up",
    bars: [9, 14, 20],
    status: "Normal",
    statusType: "normal",
    detailDate: "17 Şubat 2026",
  },
];

export default function LabResults() {
  const { t } = useTranslate();
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>("all");
  const [openDetailForCard, setOpenDetailForCard] = useState<string | null>(null);

  const closeLabDetail = useCallback(() => setOpenDetailForCard(null), []);

  return (
    <Box sx={{ width: "100%", height: "100%", display: "inline-flex", flexDirection: "column" }}>
      <Box
        sx={{
          alignSelf: "stretch",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          background: "#fff",
          borderRadius: "8px 8px 0 0",
          borderBottom: "1px solid #EFF0F0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: "26px",
              height: "26px",
              borderRadius: "100px",
              background: "#F5F5F5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/icons/generals/users.svg"
              alt=""
              sx={{ width: 14, height: 14, objectFit: "contain" }}
            />
          </Box>
          <Typography
            sx={{
              color: "#000",
              fontSize: 16,
              fontFamily: "Poppins",
              fontWeight: 600,
              lineHeight: "24px",
            }}
          >
            Son Laboratuvar Sonuçları
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {filterPills.map((pill) => {
            const isSelected = selectedFilter === pill.key;

            return (
              <Box
                key={pill.key}
                onClick={() => setSelectedFilter(pill.key)}
                sx={{
                  minWidth: { xs: "calc(50% - 4px)", sm: pill.minWidth },
                  height: 32,
                  px: 2,
                  py: "6px",
                  borderRadius: "100px",
                  outline: isSelected ? "1px solid #2196F3" : "1px solid #ECEDF2",
                  outlineOffset: "-1px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                <Typography
                  sx={{
                    color: isSelected ? "#2979C3" : "#000",
                    fontSize: 11,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    lineHeight: "20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pill.label}
                </Typography>
              </Box>
            );
          })}

          <Box
            sx={{
              height: 32,
              px: 1.25,
              py: "6px",
              background: "#fff",
              outline: "1px solid #ECEDF2",
              outlineOffset: "-1px",
              borderRadius: "100px",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontSize: 11,
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              Son 4 Kayıt
            </Typography>
            <KeyboardArrowDownRoundedIcon sx={{ width: 14, height: 14, color: "#172A41" }} />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          alignSelf: "stretch",
          p: 3,
          background: "#F7F9FB",
          outline: "1px solid #EAEDF4",
          outlineOffset: "-0.5px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRadius: "0 0 8px 8px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            width: "100%",
          }}
        >
          {labCards.map((card) => (
            <Box
              key={card.title}
              sx={{
                minHeight: 352,
                background: "#fff",
                boxShadow: "0px 10px 24px -8px rgba(42, 51, 70, 0.03), 0px 0px 0px 1px rgba(0, 0, 0, 0.07)",
                borderRadius: "12px",
                overflow: "hidden",
                display: "inline-flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  alignSelf: "stretch",
                  height: 46,
                  px: 2,
                  py: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    flex: "1 1 0",
                    color: "#0A0D14",
                    fontSize: 12,
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    lineHeight: "16.8px",
                  }}
                >
                  {card.title}
                </Typography>
                <IconButton
                  type="button"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDetailForCard(card.title);
                  }}
                  aria-label={t("nurse.patient-table.lab-request-detail.expand-main-aria")}
                  sx={{
                    height: 26,
                    width: 40,
                    p: 0,
                    borderRadius: "6px",
                    background: "#fff",
                    boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.09)",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/icons/patient-table/ci_expand.svg"
                    alt=""
                    sx={{ width: 22, height: 22, objectFit: "contain", display: "block" }}
                  />
                </IconButton>
              </Box>

              <LabGraph
                value={card.value}
                trend={card.trend}
                direction={card.direction}
                bars={card.bars}
                status={card.status}
                statusType={card.statusType}
                detailDate={card.detailDate}
                detailItems={card.detailItems}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog
        open={openDetailForCard !== null}
        onClose={closeLabDetail}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "#fff",
            borderRadius: 2,
            maxHeight: "min(88vh, 820px)",
            width: "100%",
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: "auto" }}>
          {openDetailForCard !== null && (
            <LabRequestDetail key={openDetailForCard} onClose={closeLabDetail} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
